import { ArrowRight } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { LocalizedAnchor } from '@/components/LocalizedLink';

interface Outcome {
  key: string;
  /** Deep-dive page for readers who want the full detail. */
  href: string;
}

/**
 * The decision-maker section: what LogPulse means for the business, in plain
 * language. Every block links to a dedicated page that carries the technical
 * depth, so the homepage itself stays readable for a non-technical audience.
 */
/**
 * Ordered by how strongly each driver is actually evidenced, not by how much
 * we like it. Pressure from your own customers comes first: it is the one
 * motive whose whole chain is officially documented (supply-chain security is
 * a duty of care measure, and buyers may demand proof of it), and it is the
 * one a reader can act on. The cost argument moved to the pricing section —
 * it compares against a bill the reader may not have yet.
 */
const outcomes: Outcome[] = [
  { key: 'supplychain', href: '/guides/iso-27001-logging-requirements' },
  { key: 'proactive', href: '/service-intelligence' },
  { key: 'security', href: '/siem' },
  { key: 'compliance', href: '/compliance/nis2' },
];

export function OutcomesSection() {
  const t = useT();

  return (
    <section className="py-20 sm:py-28 bg-gray-50 dark:bg-gray-900/40 border-y border-gray-200 dark:border-gray-800" aria-labelledby="outcomes-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl animate-on-scroll">
          <p className="eyebrow">{t('outcomes.eyebrow')}</p>
          <h2
            id="outcomes-heading"
            className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white"
          >
            {t('outcomes.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            {t('outcomes.subtitle')}
          </p>
        </div>

        {/* Hairline-divided grid: reads like a report, not a card wall */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 border-t border-gray-200 dark:border-gray-800">
          {outcomes.map((outcome) => (
            <div
              key={outcome.key}
              className="animate-on-scroll border-b border-gray-200 dark:border-gray-800 md:odd:border-r md:odd:pr-10 md:even:pl-10 py-10"
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t(`outcomes.${outcome.key}.title`)}
              </h3>
              <p className="mt-3 text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                {t(`outcomes.${outcome.key}.description`)}
              </p>
              <LocalizedAnchor
                href={outcome.href}
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                {t(`outcomes.${outcome.key}.link`)}
                <ArrowRight className="h-4 w-4" />
              </LocalizedAnchor>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
